import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Alert, Link } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../config/api';
import AuthPageFrame from '../components/AuthPageFrame';
import AuthLegalNotice from '../components/AuthLegalNotice';
import SessionReplacementDialog, { SessionReplacementProof, SessionReplacementProofType } from '../components/SessionReplacementDialog';

const HighSchoolLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [replacementChallenge, setReplacementChallenge] = useState<string | null>(null);
  const [replacementProofType, setReplacementProofType] = useState<SessionReplacementProofType>('password');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, confirmSessionReplacement } = useAuth();

  useEffect(() => {
    try {
      localStorage.removeItem('admin_auth_token');
      localStorage.removeItem('admin_user');
    } catch (e) {
      // ignore
    }
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const submit = async () => {
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      setLoading(false);
      finishLogin();
    } catch (err: any) {
      setLoading(false);
      if (err?.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(err.response.data.challengeToken);
        setReplacementProofType(err.response.data.proofType || 'password');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Login failed');
      }
    }
  };

  const finishLogin = () => {
    setReplacementChallenge(null);
    navigate('/high-school');
  };

  const confirmReplacement = async (proof: SessionReplacementProof) => {
    if (!replacementChallenge) return;
    setLoading(true);
    setError(null);
    try {
      await confirmSessionReplacement(replacementChallenge, proof);
      finishLogin();
    } catch (err: any) {
      if (err?.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(err.response.data.challengeToken);
        setReplacementProofType(err.response.data.proofType || replacementProofType);
        setError(null);
      } else if (err?.response?.data?.code === 'INVALID_CONFIRMATION_CREDENTIALS') {
        setError(err.response.data.message || 'The password is incorrect.');
      } else if (err?.response?.data?.code === 'SESSION_REPLACEMENT_EXPIRED') {
        setReplacementChallenge(null);
        setError(err.response.data.message || 'The sign-in confirmation expired. Please sign in again.');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Could not sign in on this device');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <AuthPageFrame mode="login">
        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 1.2 }}>
          STUDENT ACCESS
        </Typography>
        <Typography component="h1" variant="h4" sx={{ mt: 0.5, mb: 1, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700 }}>
          Sign in
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
          Continue to your High School learning dashboard.
        </Typography>
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          fullWidth
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          fullWidth
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<GoogleIcon sx={{ color: 'white' }} />}
            onClick={() => {
              const googleParams = new URLSearchParams({
                source: 'high-school',
                redirect: '/high-school/login?source=google',
              });
              window.location.href = `${API_BASE_URL}/auth/google?${googleParams.toString()}`;
            }}
            sx={{
              mb: 1,
              textTransform: 'none',
              fontWeight: 700,
              color: 'white',
              background: 'linear-gradient(90deg, #4285F4 0%, #34A853 50%, #FBBC05 100%)',
              borderRadius: 2,
              boxShadow: '0 8px 12px rgba(66, 133, 244, 0.35)',
              py: 1.1,
              '&:hover': {
                background: 'linear-gradient(90deg, #3367D6 0%, #2E8C4F 50%, #E6A400 100%)',
                boxShadow: '0 10px 16px rgba(0, 0, 0, 0.35)',
              },
              '& .MuiButton-startIcon': {
                marginRight: 8,
              }
            }}
          >
            Continue with Google
          </Button>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Link
            component={RouterLink}
            to="/forgot-password"
            sx={{ textDecoration: 'none', fontSize: '0.875rem', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
          >
            Forgot Password?
          </Link>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'auto auto' }, gap: 1.5, alignItems: 'center' }}>
          <Button variant="contained" onClick={submit} disabled={loading} size="large" sx={{ minHeight: 48, textTransform: 'none', fontWeight: 700 }}>
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>
          <Button variant="text" onClick={() => navigate('/high-school/signup')}>
            Create an account
          </Button>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Link component={RouterLink} to="/parent/login" sx={{ textDecoration: 'none' }}>
            Parent / Guardian login
          </Link>
        </Box>
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <AuthLegalNotice />
        </Box>
    </AuthPageFrame>
    <SessionReplacementDialog
      open={Boolean(replacementChallenge)}
      proofType={replacementProofType}
      loading={loading}
      error={error}
      onConfirm={confirmReplacement}
      onCancel={() => { setReplacementChallenge(null); setError(null); }}
    />
    </>
  );
};

export default HighSchoolLogin;
